import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Check,
  X,
  Star,
  Sparkles,
  ArrowRight,
  Crown,
  Zap,
  DollarSign,
  Image as ImageIcon,
  Trophy,
} from 'lucide-react';

type Tool = {
  rank: number;
  name: string;
  slug: string;
  tagline: string;
  bestFor: string;
  startingPrice: string;
  pricePerImage: string;
  turnaround: string;
  rating: number;
  highlight?: boolean;
  pros: string[];
  cons: string[];
  summary: string;
  ctaHref: string;
  ctaLabel: string;
};

const tools: Tool[] = [
  {
    rank: 1,
    name: 'ListBright',
    slug: 'listbright',
    tagline: 'The all-in-one AI listing suite — virtual staging plus everything else a listing needs.',
    bestFor: 'Agents and owners who want staging + photos + description + video + microsite from one upload',
    startingPrice: '$15 one-time / $30 mo',
    pricePerImage: 'Included in plan',
    turnaround: 'Under 5 minutes',
    rating: 4.9,
    highlight: true,
    pros: [
      'AI virtual staging in multiple styles (modern, traditional, minimalist, Scandinavian)',
      'Bundled with photo enhancement, declutter, golden hour, sky replacement, lawn replacement',
      'Auto-generates listing description, cinematic video, microsite, flyer and QR code from the same upload',
      'Whole-listing pricing — no per-image fees',
      'Single Property Kit for owners selling one home ($15 one-time)',
    ],
    cons: [
      'Best ROI when you use the full suite, not staging in isolation',
      'Newer brand vs decade-old competitors',
    ],
    summary:
      'ListBright is the only tool on this list that handles virtual staging as part of a complete listing kit. You upload photos once and walk away with staged images, an enhanced photo set, a written description, a cinematic video, a microsite and a flyer — all consistent, all branded. For anyone marketing a property end-to-end, that bundle beats paying for staging, copy, video and a microsite separately.',
    ctaHref: '/auth',
    ctaLabel: 'Try ListBright Free',
  },
  {
    rank: 2,
    name: 'BoxBrownie',
    slug: 'boxbrownie',
    tagline: 'Human-edited virtual staging with a long real estate track record.',
    bestFor: 'Agencies that prefer human editors and don\'t mind 24-48h turnaround',
    startingPrice: '$32 per image',
    pricePerImage: '$32',
    turnaround: '24–48 hours',
    rating: 4.5,
    pros: [
      'Hand-edited results are consistently high quality',
      'Established brand trusted by large brokerages',
      'Offers a wide range of additional photo edits',
    ],
    cons: [
      'Among the most expensive options per image',
      'Overnight turnaround feels slow vs AI tools',
      'Staging only — no description, video or microsite',
    ],
    summary:
      'BoxBrownie is the legacy choice. Quality is reliably good because real editors do the work, but you pay for it in both dollars and time. If you only need a handful of staged hero shots and aren\'t price-sensitive, it\'s a safe pick.',
    ctaHref: '/compare/boxbrownie',
    ctaLabel: 'See ListBright vs BoxBrownie',
  },
  {
    rank: 3,
    name: 'Virtual Staging AI',
    slug: 'virtual-staging-ai',
    tagline: 'Fast, AI-only virtual staging at a low per-image rate.',
    bestFor: 'Solo agents who only need staging and want the cheapest per-image price',
    startingPrice: '$15 per month',
    pricePerImage: '~$1.50',
    turnaround: 'Seconds',
    rating: 4.3,
    pros: [
      'Very cheap per image at scale',
      'Near-instant AI rendering',
      'Simple, no-frills interface',
    ],
    cons: [
      'Furniture placement can look generic on tricky room shapes',
      'No photo enhancement, description, video or microsite',
      'Watermarks on lowest tier',
    ],
    summary:
      'Virtual Staging AI is purpose-built for one job and prices it aggressively. If staging is the only gap in your workflow and you already have copy, photos and video covered, it\'s a strong narrow pick.',
    ctaHref: '/compare/virtual-staging-ai',
    ctaLabel: 'See ListBright vs Virtual Staging AI',
  },
  {
    rank: 4,
    name: 'ApplyDesign',
    slug: 'applydesign',
    tagline: 'AI staging with style presets and clean room recognition.',
    bestFor: 'Photographers who want bulk staging credits',
    startingPrice: '$19 / 10 images',
    pricePerImage: '~$1.90',
    turnaround: 'Minutes',
    rating: 4.2,
    pros: ['Decent style variety', 'Pay-as-you-go credit packs', 'Free trial credits'],
    cons: [
      'Quality varies by room type',
      'Staging-only product',
      'Less polished UI than newer tools',
    ],
    summary:
      'ApplyDesign sits in the middle of the pack — affordable AI staging with reasonable output. A practical pick if you want credits rather than a subscription, but it doesn\'t extend beyond staging.',
    ctaHref: '/compare/apply-design',
    ctaLabel: 'See ListBright vs ApplyDesign',
  },
  {
    rank: 5,
    name: 'Styldod',
    slug: 'styldod',
    tagline: 'Hybrid AI + human virtual staging service.',
    bestFor: 'Brokerages that want managed staging across many listings',
    startingPrice: '$16 per image',
    pricePerImage: '$16',
    turnaround: '12–24 hours',
    rating: 4.4,
    pros: ['Strong quality control', 'Wide style catalog', 'Bundled photo editing add-ons'],
    cons: [
      'Slower than pure-AI tools',
      'Pricing escalates with revisions',
      'No description, video or microsite output',
    ],
    summary:
      'Styldod blends AI rendering with human review, landing on solid quality at mid-tier pricing. Good for teams that want managed staging without the BoxBrownie price tag.',
    ctaHref: '/compare',
    ctaLabel: 'Compare alternatives',
  },
  {
    rank: 6,
    name: 'VirtualStagingSolutions',
    slug: 'virtualstagingsolutions',
    tagline: 'Premium human-edited staging for luxury listings.',
    bestFor: 'Luxury listings where photorealism matters more than speed or price',
    startingPrice: '$59 per image',
    pricePerImage: '$59',
    turnaround: '24–72 hours',
    rating: 4.6,
    pros: ['Top-tier photorealism', 'Designer-led furniture selection', 'Handles complex spaces well'],
    cons: ['Highest price point on this list', 'Slowest turnaround', 'Overkill for everyday listings'],
    summary:
      'When the listing is a multi-million-dollar property and the photos will run in glossy print, VirtualStagingSolutions earns its premium. For routine inventory, it\'s more than most agents need.',
    ctaHref: '/compare',
    ctaLabel: 'Compare alternatives',
  },
  {
    rank: 7,
    name: 'RoOomy',
    slug: 'rooomy',
    tagline: '3D-focused virtual staging with AR previews.',
    bestFor: 'New-build and developer marketing with 3D walkthrough needs',
    startingPrice: 'Custom (enterprise)',
    pricePerImage: 'Custom',
    turnaround: 'Varies',
    rating: 4.1,
    pros: ['3D and AR capabilities', 'Strong for new construction', 'Furniture brand partnerships'],
    cons: [
      'Not transparent pricing',
      'Overengineered for a single resale listing',
      'Slower onboarding than self-serve AI tools',
    ],
    summary:
      'RoOomy is built for developers and big-ticket new construction marketing rather than individual agents. Powerful, but a heavier lift than most listings warrant.',
    ctaHref: '/compare/rooomy',
    ctaLabel: 'See ListBright vs RoOomy',
  },
  {
    rank: 8,
    name: 'Collov AI',
    slug: 'collov-ai',
    tagline: 'Interior design AI with virtual staging features bolted on.',
    bestFor: 'Designers and stagers who also do real estate work occasionally',
    startingPrice: '$19 / mo',
    pricePerImage: 'Subscription',
    turnaround: 'Seconds',
    rating: 4.0,
    pros: ['Designer-oriented style controls', 'Good for mood-boarding', 'Fast AI rendering'],
    cons: [
      'Not purpose-built for real estate listings',
      'No listing description, video or microsite',
      'Quality on empty real-listing rooms is hit or miss',
    ],
    summary:
      'Collov is really an interior-design tool that happens to do staging. Useful if you live in interior design workflows, but light on the real estate marketing side.',
    ctaHref: '/compare/collov',
    ctaLabel: 'See ListBright vs Collov',
  },
];

const criteria = [
  {
    title: 'Output quality',
    body: 'Photorealism, furniture scale, shadows, and how well the tool handles tricky rooms (angled walls, mirrors, sliding doors).',
  },
  {
    title: 'Turnaround speed',
    body: 'Time from upload to staged image. AI tools win on speed; human-edited services win on consistency.',
  },
  {
    title: 'Price per listing',
    body: 'Real cost to fully stage a typical 8–12-photo listing — not just the headline per-image price.',
  },
  {
    title: 'What else you get',
    body: 'Does the tool stop at staging, or does it also handle photo enhancement, descriptions, video, microsite, and flyer? Bundled suites compound time savings.',
  },
  {
    title: 'Ease of use',
    body: 'How quickly a non-technical agent can upload, get a result, and publish — without learning a design app.',
  },
];

const useCases = [
  {
    label: 'Selling a single home as the owner',
    pick: 'ListBright Single Property Kit',
    why: 'One $15 payment covers staging plus the description, photos, video and microsite you need to list anywhere.',
  },
  {
    label: 'Full-time agent listing 4+ homes a month',
    pick: 'ListBright Growth',
    why: 'Per-listing economics beat paying $16–$32 per image for staging alone, and you get the rest of the marketing kit.',
  },
  {
    label: 'Just need a few hero shots staged, nothing else',
    pick: 'Virtual Staging AI',
    why: 'Cheapest per-image option when you genuinely only need staging and have copy/video covered elsewhere.',
  },
  {
    label: 'Luxury listing where photoreal matters more than budget',
    pick: 'VirtualStagingSolutions',
    why: 'Hand-edited results justify the premium when the listing price supports it.',
  },
  {
    label: 'Large brokerage with managed staging needs',
    pick: 'Styldod or BoxBrownie',
    why: 'Hybrid or human-edited services scale predictably across many agents and listings.',
  },
];

const faqs = [
  {
    q: 'What is the best virtual staging software in 2026?',
    a: 'For most agents and owners, ListBright is the best overall pick because it bundles AI virtual staging with photo enhancement, listing description, cinematic video, microsite and flyer — all from one upload, at a fixed per-listing price. For staging-only needs, Virtual Staging AI is the cheapest per image, and BoxBrownie remains the leader for human-edited results.',
  },
  {
    q: 'How much does AI virtual staging cost?',
    a: 'AI-only tools range from roughly $1–$3 per image. Human-edited services like BoxBrownie cost $32 per image, and premium services like VirtualStagingSolutions cost $59+. Suite tools like ListBright bundle staging into a per-listing price (from $15 one-time or $30/mo) that typically covers an entire listing rather than charging per image.',
  },
  {
    q: 'Is AI virtual staging legal?',
    a: 'Yes, AI virtual staging is legal in every major market, but most MLS rules and the NAR Code of Ethics require staged photos to be clearly labeled — for example, "Virtually Staged" in the caption. The tool you use doesn\'t change the disclosure requirement; the disclosure is on the agent.',
  },
  {
    q: 'How long does virtual staging take?',
    a: 'AI virtual staging takes seconds to a few minutes. ListBright produces a fully staged set plus the rest of the listing kit in under 5 minutes. Human-edited services like BoxBrownie and VirtualStagingSolutions typically take 24–72 hours.',
  },
  {
    q: 'Can virtual staging replace physical staging?',
    a: 'For online listings, yes — every buyer searches online first, and staged photos drive clicks and showings just as effectively. Physical staging still matters for in-person walkthroughs of vacant high-end homes, but for the photo-driven part of the funnel virtual staging is faster, dramatically cheaper, and easier to re-style.',
  },
  {
    q: 'What\'s the difference between virtual staging and photo enhancement?',
    a: 'Photo enhancement improves what\'s already in the photo (lighting, sky, lawn, clutter). Virtual staging adds furniture and decor to empty or sparsely furnished rooms. Most agents need both, which is why bundled suites like ListBright are usually more economical than buying each capability separately.',
  },
  {
    q: 'Does ListBright work for empty rooms?',
    a: 'Yes. ListBright\'s virtual staging is designed to furnish empty rooms with realistic furniture, lighting and shadows in multiple styles (modern, traditional, minimalist, Scandinavian). It also handles partially furnished rooms by decluttering first, then staging.',
  },
];

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < Math.round(rating)
              ? 'fill-amber-400 text-amber-400'
              : 'text-muted-foreground/30'
          }`}
        />
      ))}
      <span className="text-sm font-semibold ml-1">{rating.toFixed(1)}</span>
    </div>
  );
}

export default function BestVirtualStagingSoftware() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Best Virtual Staging Software (2026): 8 Tools Compared',
      datePublished: '2026-06-18',
      dateModified: '2026-06-29',
      author: { '@type': 'Organization', name: 'ListBright' },
      publisher: {
        '@type': 'Organization',
        name: 'ListBright',
        logo: {
          '@type': 'ImageObject',
          url: 'https://listbright.ai/logo.png',
        },
      },
      mainEntityOfPage: 'https://listbright.ai/best-virtual-staging-software',
      description:
        'Independent comparison of the 8 best virtual staging software tools in 2026, ranked on quality, speed, price and what else you get beyond staging.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Best Virtual Staging Software 2026',
      itemListElement: tools.map((t) => ({
        '@type': 'ListItem',
        position: t.rank,
        item: {
          '@type': 'SoftwareApplication',
          name: t.name,
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description: t.tagline,
          offers: { '@type': 'Offer', price: t.startingPrice, priceCurrency: 'USD' },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: t.rating.toFixed(1),
            bestRating: '5',
            ratingCount: '1',
          },
          review: {
            '@type': 'Review',
            author: { '@type': 'Organization', name: 'ListBright Editorial' },
            datePublished: '2026-06-29',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: t.rating.toFixed(1),
              bestRating: '5',
            },
            reviewBody: t.summary,
          },
        },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="Best Virtual Staging Software (2026): 8 Tools Compared"
        description="Independent comparison of the 8 best virtual staging software tools in 2026 — ranked on quality, speed, price and what you get beyond staging."
        canonical="https://listbright.ai/best-virtual-staging-software"
        jsonLd={jsonLd}
        breadcrumbs={[
          { label: 'Compare', path: '/compare' },
          { label: 'Best Virtual Staging Software', path: '/best-virtual-staging-software' },
        ]}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16 lg:py-24">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <Badge variant="secondary" className="mb-4 px-4 py-2">
            <Trophy className="w-4 h-4 mr-2" />
            Updated June 2026 · Independently reviewed
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6 leading-tight">
            Best Virtual Staging Software <span className="text-gradient">(2026)</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
            We evaluated 8 virtual staging tools across output quality, turnaround speed, price per
            listing and what else you get beyond staging. Here's how they stack up — and which one
            wins for your situation.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg">
              <Link to="/auth">
                Try the #1 pick free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href="#comparison-table">See the full comparison</a>
            </Button>
          </div>
        </div>
      </section>

      {/* TL;DR */}
      <section className="py-12 lg:py-16 border-y border-border/40 bg-muted/30">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-display font-bold mb-6 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> The short answer
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { label: 'Best overall', value: 'ListBright', why: 'Staging + the rest of the listing kit, one fixed price.' },
              { label: 'Best for staging-only', value: 'Virtual Staging AI', why: 'Cheapest per image when staging is the only gap.' },
              { label: 'Best human-edited', value: 'BoxBrownie', why: 'Reliable, hand-edited quality if budget allows.' },
              { label: 'Best for luxury listings', value: 'VirtualStagingSolutions', why: 'Premium photorealism for high-ticket homes.' },
            ].map((row) => (
              <div key={row.label} className="rounded-xl border border-border/40 bg-card p-5">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                  {row.label}
                </div>
                <div className="text-lg font-semibold mb-1">{row.value}</div>
                <p className="text-sm text-muted-foreground">{row.why}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How we evaluated */}
      <section className="py-16 lg:py-20">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-display font-bold mb-3">How we evaluated</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl">
            Every tool below was scored against the same five criteria — the ones that actually
            change a listing's outcome, not vanity features.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {criteria.map((c) => (
              <div key={c.title} className="rounded-xl border border-border/40 bg-card p-5">
                <h3 className="font-semibold mb-1">{c.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section id="comparison-table" className="py-16 lg:py-20 bg-muted/30 border-y border-border/40">
        <div className="container max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-display font-bold mb-8 text-center">
            At-a-glance comparison
          </h2>
          <div className="overflow-x-auto rounded-2xl border border-border/40 bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-left">
                  <th className="px-4 py-3 font-semibold">#</th>
                  <th className="px-4 py-3 font-semibold">Tool</th>
                  <th className="px-4 py-3 font-semibold">Best for</th>
                  <th className="px-4 py-3 font-semibold">Starting price</th>
                  <th className="px-4 py-3 font-semibold">Per image</th>
                  <th className="px-4 py-3 font-semibold">Turnaround</th>
                  <th className="px-4 py-3 font-semibold">Rating</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((t) => (
                  <tr
                    key={t.slug}
                    className={`border-t border-border/40 ${
                      t.highlight ? 'bg-primary/5' : ''
                    }`}
                  >
                    <td className="px-4 py-4 font-bold">{t.rank}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 font-semibold">
                        {t.highlight && <Crown className="w-4 h-4 text-primary" />}
                        {t.name}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-muted-foreground max-w-xs">{t.bestFor}</td>
                    <td className="px-4 py-4 font-medium">{t.startingPrice}</td>
                    <td className="px-4 py-4">{t.pricePerImage}</td>
                    <td className="px-4 py-4">{t.turnaround}</td>
                    <td className="px-4 py-4">
                      <Stars rating={t.rating} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Detailed reviews */}
      <section className="py-16 lg:py-24">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-display font-bold mb-10 text-center">
            The 8 best virtual staging tools, ranked
          </h2>
          <div className="space-y-10">
            {tools.map((t) => (
              <article
                key={t.slug}
                className={`rounded-2xl border p-6 sm:p-8 ${
                  t.highlight
                    ? 'border-primary/40 bg-primary/5 shadow-lg'
                    : 'border-border/40 bg-card'
                }`}
              >
                <header className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={t.highlight ? 'default' : 'secondary'}>
                        #{t.rank} {t.highlight ? '· Editor\'s pick' : ''}
                      </Badge>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-display font-bold mb-1 flex items-center gap-2">
                      {t.highlight && <Crown className="w-6 h-6 text-primary" />}
                      {t.name}
                    </h3>
                    <p className="text-muted-foreground">{t.tagline}</p>
                  </div>
                  <Stars rating={t.rating} />
                </header>

                <div className="grid sm:grid-cols-3 gap-3 mb-6">
                  <div className="rounded-lg bg-background/60 border border-border/40 p-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-1">
                      <DollarSign className="w-3.5 h-3.5" /> Price
                    </div>
                    <div className="font-semibold text-sm">{t.startingPrice}</div>
                  </div>
                  <div className="rounded-lg bg-background/60 border border-border/40 p-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-1">
                      <Zap className="w-3.5 h-3.5" /> Turnaround
                    </div>
                    <div className="font-semibold text-sm">{t.turnaround}</div>
                  </div>
                  <div className="rounded-lg bg-background/60 border border-border/40 p-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-1">
                      <ImageIcon className="w-3.5 h-3.5" /> Per image
                    </div>
                    <div className="font-semibold text-sm">{t.pricePerImage}</div>
                  </div>
                </div>

                <p className="leading-relaxed mb-6">{t.summary}</p>

                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-600" /> Pros
                    </h4>
                    <ul className="space-y-1.5">
                      {t.pros.map((p) => (
                        <li key={p} className="text-sm text-muted-foreground flex gap-2">
                          <span className="text-emerald-600 shrink-0">+</span>
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <X className="w-4 h-4 text-muted-foreground" /> Cons
                    </h4>
                    <ul className="space-y-1.5">
                      {t.cons.map((c) => (
                        <li key={c} className="text-sm text-muted-foreground flex gap-2">
                          <span className="text-muted-foreground shrink-0">−</span>
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button asChild variant={t.highlight ? 'default' : 'outline'}>
                    <Link to={t.ctaHref}>
                      {t.ctaLabel}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Which should you choose */}
      <section className="py-16 lg:py-20 bg-muted/30 border-y border-border/40">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-display font-bold mb-3 text-center">
            Which should you choose?
          </h2>
          <p className="text-muted-foreground mb-8 text-center max-w-2xl mx-auto">
            Pick by your situation, not by feature list.
          </p>
          <div className="space-y-3">
            {useCases.map((u) => (
              <div
                key={u.label}
                className="rounded-xl border border-border/40 bg-card p-5 sm:flex sm:items-center sm:gap-6"
              >
                <div className="sm:w-1/2 mb-2 sm:mb-0">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                    If you're…
                  </div>
                  <div className="font-semibold">{u.label}</div>
                </div>
                <div className="sm:w-1/2">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                    Pick
                  </div>
                  <div className="font-semibold text-primary mb-1">{u.pick}</div>
                  <p className="text-sm text-muted-foreground">{u.why}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20">
        <div className="container max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-display font-bold mb-8 text-center">
            Frequently asked questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/10 via-background to-accent/5">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
            Skip the per-image bill. Get the whole listing.
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            ListBright stages your photos, enhances them, writes the description, generates a
            cinematic video, builds a microsite and prints a flyer — from one upload, in under five
            minutes.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg">
              <Link to="/auth">
                Try ListBright free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/single-property-kit">See the $15 Single Property Kit</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}